import { useEffect, useMemo } from 'react'
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { YStack, XStack, Text } from 'tamagui'
import TopTabs from './components/TopTabs'
import TopHeader from './components/TopHeader'
import UniversalButton from './components/UniversalButton'
import ChatIndex from './routes/chat/index'
import ChatChannel from './routes/chat/[channelId]'
import Channels from './routes/chat/channels'
import ChatDmRedirect from './routes/chat/dm'
import ComposeComment from './components/chat/groupDiscussion/ComposeComment'
import Events from './routes/events'
import Marketplace from './routes/marketplace'
import PlacesList from './routes/places/index'
import PlaceDetail from './routes/places/[id]'
import AddPlace from './routes/places/add'
import ComposePlaceComment from './components/places/ComposePlaceComment'
import Profile from './routes/profile'
import PublicProfile from './routes/public-profile/[id]'
import ManagePublicProfile from './routes/public-profile/manage'
import BlockedUsers from './routes/profile/blocked-users'
import { useSession } from './lib/session/SessionProvider'
import Groups from './routes/groups'
import GroupDetail from './routes/groups/[id]'
import GroupInfo from './routes/groups/info'
import SuggestGroupScreen from './routes/groups/suggest-group'
import Perks from './routes/perks'
import CreateGroupEventPage from './routes/events/create-group-event'
import NotificationsScreen from './routes/events/notifications'
import EventDetailScreen from './routes/events/[id]'
import PerkDetail from './routes/perks/[id]'
import ReferralPerkDetail from './routes/perks/referral/[id]'
import WebViewPage from './webview'
import CustomizeReferralMessageScreen from './routes/perks/referral/customize-message'
import AllMembers from './routes/chat/all-members'
import WelcomeScreen from './components/onboarding/WelcomeScreen'
import DeactivatedAccountScreen from './components/ui/deactivated-user'
import { CobuHeader, CobuBackButton, CobuButton } from '@cobu/ui'
// Removed page-specific header icons from here; moved to notifications page
import { Strings } from './constants/Strings'
import { getFloatingBottomOffset } from './constants/layout'

export default function AppShell() {
  const session = useSession()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const location = useLocation()
  const buildingConfig = session?.buildingConfig

  const tabs = useMemo(() => {
    const tabLabels = Strings.get().appShell.tabs
    const baseTabs = [
      { to: '/chat', label: tabLabels.feed, hidden: false },
      { to: '/events', label: tabLabels.events, hidden: buildingConfig?.hideEvents === true },
      { to: '/groups', label: tabLabels.groups, hidden: buildingConfig ? !buildingConfig.showGroups : false },
      { to: '/marketplace', label: tabLabels.marketplace, hidden: false },
      // Off by default; gated per-building by the showPlaces flag from BuildingDashboardConfig.
      { to: '/places', label: tabLabels.places, hidden: !buildingConfig?.showPlaces },
      { to: '/perks', label: tabLabels.perks, hidden: buildingConfig?.hidePerks === true },
      { to: '/profile', label: tabLabels.profile, hidden: false },
    ]

    return baseTabs
      .filter((tab) => !tab.hidden)
      .map(({ hidden, ...tab }) => tab)
  }, [buildingConfig])

  const defaultTab = tabs[0]?.to ?? '/chat'

  const normalizePath = useMemo(() => {
    return (path: string) => {
      if (!path) return '/'
      const normalized = path.replace(/\/+$/, '')
      return normalized === '' ? '/' : normalized
    }
  }, [])

  const normalizedPathname = normalizePath(pathname)
  const mainTabHomePaths = useMemo(() => tabs.map((tab) => normalizePath(tab.to)), [tabs, normalizePath])
  const shouldShowUniversalButton = mainTabHomePaths.includes(normalizedPathname)

  // The notification center is global chrome, not an events feature. Its legacy
  // path still lives under /events, so it has to survive the restriction guard —
  // otherwise buildings with hideEvents can never open the notification bell.
  const isRestrictedPath = useMemo(() => {
    const exemptPaths = ['/events/notifications']

    return (path: string, prefixes: string[]) => {
      if (exemptPaths.includes(path)) return false
      // Match on segment boundaries so /events never swallows a sibling like /eventsomething.
      return prefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
    }
  }, [])

  const restrictedPrefixes = useMemo(() => {
    const prefixes: string[] = []

    if (buildingConfig && !buildingConfig.showGroups) {
      prefixes.push('/groups')
    }

    if (buildingConfig?.hidePerks === true) {
      prefixes.push('/perks')
    }

    if (buildingConfig?.hideEvents === true) {
      prefixes.push('/events')
    }

    return prefixes
  }, [buildingConfig])

  useEffect(() => {
    if (!restrictedPrefixes.length) return

    if (isRestrictedPath(normalizePath(location.pathname), restrictedPrefixes)) {
      navigate(defaultTab, { replace: true })
    }
  }, [restrictedPrefixes, location.pathname, navigate, defaultTab, isRestrictedPath, normalizePath])

  const user = session?.user;

  // One gate, one screen. The code of conduct now rides along with the terms checkbox,
  // so `agreedToCoC` no longer blocks anyone on its own.
  if (user?.acceptedTermsAndConditions === false) {
    return <WelcomeScreen />;
  }

  if (user?.deactivated === true) {
    return <DeactivatedAccountScreen />;
  }


  return (
    <YStack flex={1} minHeight="100dvh" width="100%" maxWidth="100%" ov="hidden">
      <YStack flex={1} width="100%" maxWidth="100%" ov="hidden">
        <TopHeader />
        {session?.dev && (
          <XStack justifyContent="center" alignItems="center" paddingVertical="$2" backgroundColor="#fde68a">
            <Text fontWeight="700">{`DEV TOKEN`}</Text>
          </XStack>
        )}
        <YStack
          flex={1}
          width="100%"
          maxWidth="100%"
          ov="scroll"
          style={{
            paddingBottom: getFloatingBottomOffset(24),
          }}
        >
          <Routes>
            <Route path="/" element={<Navigate to={defaultTab} replace />} />
            <Route path="/chat" element={<ChatIndex />} />
            <Route path="/chat/channels" element={<Channels />} />
            <Route path="/chat/:channelId" element={<ChatChannel />} />
            <Route path="/chat/dm/:userId" element={<ChatDmRedirect />} />
            <Route path="/chat/compose-comment" element={<ComposeComment />} />
            <Route path="/events/compose-comment" element={<ComposeComment />} />
            <Route path="/chat/all-members" element={<AllMembers />} />
                            <Route path="/events" element={<Events />} />
                <Route path="/notifications" element={<NotificationsScreen />} />
                {/* Legacy path — kept for in-flight deeplinks and older host builds. */}
                <Route path="/events/notifications" element={<Navigate to="/notifications" replace />} />
                            <Route path="/groups" element={<Groups />} />
            <Route path="/groups/info" element={<GroupInfo />} />
            <Route path="/groups/:id" element={<GroupDetail />} />
            <Route path="/groups/suggest-group" element={<SuggestGroupScreen />} />
            <Route path="/marketplace" element={<Marketplace />} />
            {buildingConfig?.showPlaces && (
              <>
                <Route path="/places" element={<PlacesList />} />
                <Route path="/places/add" element={<AddPlace />} />
                <Route path="/places/:id/compose-comment" element={<ComposePlaceComment />} />
                <Route path="/places/:id" element={<PlaceDetail />} />
              </>
            )}
            {buildingConfig?.hidePerks !== true && (
              <>
                <Route path="/perks" element={<Perks />} />
                <Route path="/perks/:id" element={<PerkDetail />} />
                <Route path="/perks/referral" element={<ReferralPerkDetail />} />
                <Route path="/perks/referral/customize-message" element={<CustomizeReferralMessageScreen />} />
              </>
            )}
            <Route path="/webview" element={<WebViewPage />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/profile/:id" element={<Profile />} />
            <Route path="/events/create-group-event" element={<CreateGroupEventPage />} />
            <Route path="/events/:id" element={<EventDetailScreen />} />
            <Route path="/profile/blocked-users" element={<BlockedUsers />} />
            <Route path="/public-profile/:id" element={<PublicProfile />} />
            <Route path="/public-profile/manage" element={<ManagePublicProfile />} />
          </Routes>
        </YStack>
        {shouldShowUniversalButton ? <UniversalButton /> : null}
      </YStack>
      <TopTabs tabs={tabs} />
    </YStack>
  )
}
